// src/services/cacheService.js - Fast caching for weather data

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const memoryCache = new Map();

/**
 * Memory cache with expiration
 */
export const cacheService = {
  /**
   * Set cache entry
   */
  set: (key, data, duration = CACHE_DURATION) => {
    memoryCache.set(key, {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + duration
    });
  },

  /**
   * Get cache entry
   */
  get: (key) => {
    const cached = memoryCache.get(key);

    if (!cached) return null;

    // Check if expired
    if (Date.now() > cached.expiresAt) {
      memoryCache.delete(key);
      return null;
    }

    return cached.data;
  },

  /**
   * Check if cache has valid entry
   */
  has: (key) => {
    return cacheService.get(key) !== null;
  },

  /**
   * Clear specific cache entry
   */
  clear: (key) => {
    memoryCache.delete(key);
  },

  /**
   * Clear all cache
   */
  clearAll: () => {
    memoryCache.clear();
  },

  /**
   * Get cache age in seconds
   */
  getAge: (key) => {
    const cached = memoryCache.get(key);
    if (!cached) return null;
    return Math.floor((Date.now() - cached.timestamp) / 1000);
  }
};

/**
 * Generate cache key for API requests
 */
export const generateCacheKey = (endpoint, params = {}) => {
  const sortedParams = Object.keys(params)
    .sort()
    .map(key => `${key}=${params[key]}`)
    .join('&');
  return `${endpoint}?${sortedParams}`;
};

/**
 * Cached fetch wrapper
 */
export const cachedFetch = async (url, options = {}, cacheDuration = CACHE_DURATION) => {
  const cacheKey = url;

  // Check cache first
  const cached = cacheService.get(cacheKey);
  if (cached) {
    console.log(`💾 Cache HIT (age: ${cacheService.getAge(cacheKey)}s):`, cacheKey);
    return cached;
  }

  console.log('🌐 Cache MISS - Fetching:', cacheKey);

  // Fetch from API
  const response = await fetch(url, options);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const data = await response.json();

  // Store in cache
  cacheService.set(cacheKey, data, cacheDuration);

  return data;
};

export default cacheService;
